import { useCallback, useReducer } from 'react'
import { mergeAll, isEmpty, reject, equals, reduce, assoc } from 'ramda'
import moment from 'moment'
import { useIntl } from 'react-intl'
import debounce from 'lodash.debounce'
import { useDispatch, useSelector } from 'react-redux'

import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import { TYPE_GENERAL, TYPE_EVENT, TYPE_POLL } from 'app/constants/messageType'
import {
  DELETED_MSG,
  UPDATED_MSG,
  CARD_VIEW,
  TABLE_VIEW,
  timelineFilter,
} from 'app/constants/messageDetail'
import { isEmptyOrNil } from 'libs/empty'

export const ActionType = {
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  SET_LOADER_UPDATING: 'SET_LOADER_UPDATING',
  MERGE_NOTIFICATIONS: 'MERGE_NOTIFICATIONS',
  SET_NOTIFICATION_DETAIL: 'SET_NOTIFICATION_DETAIL',
  SET_LOADER_NOTIFICATIONS: 'SET_LOADER_NOTIFICATIONS',
  SET_INSERT_NOTIFICATIONS: 'SET_INSERT_NOTIFICATIONS',
  CLEAR_NOTIFICATION_DETAIL: 'CLEAR_NOTIFICATION_DETAIL',
  SET_INFINITE_NOTIFICATIONS: 'SET_INFINITE_NOTIFICATIONS',
  SET_LOADER_INFINITE_SCROLL: 'SET_LOADER_INFINITE_SCROLL',
  SET_PLACE_EMPTY_NOTIFICATION: 'SET_PLACE_EMPTY_NOTIFICATION',
  MERGE_INFINITE_NOTIFICATIONS: 'MERGE_INFINITE_NOTIFICATIONS',
  UPDATE_INFINITE_NOTIFICATIONS: 'UPDATE_INFINITE_NOTIFICATIONS',
  SET_LOADER_NOTIFICATION_DETAIL: 'SET_LOADER_NOTIFICATION_DETAIL',
  SET_LOADER_INFINITE_NOTIFICATIONS: 'SET_LOADER_INFINITE_NOTIFICATIONS',
  REMOVE_DATA_FROM_INFINITE_NOTIFICATIONS:
    'REMOVE_DATA_FROM_INFINITE_NOTIFICATIONS',
  SET_FAVORITE: 'SET_FAVORITE',
  SET_FAVORITE_LOADING: 'SET_FAVORITE_LOADING',
}

const initialState = {
  params: {
    page: 1,
    limit: 20,
    type: `${TYPE_GENERAL},${TYPE_EVENT},${TYPE_POLL}`,
  },
  isHasMore: true,
  isUpdating: false,
  notificationDetail: {},
  isInfiniteScroll: false,
  infiniteNotifications: [],
  isEmptyNotification: false,
  isLoadingNotifications: true,
  isLoadingNotificationDetail: false,
  isFavoriteLoading: false,
  notifications: { data: [], meta: {} },
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.value,
        params: action.params,
        isLoadingNotifications: false,
        isEmptyNotification: false,
      }
    case ActionType.SET_LOADER_NOTIFICATIONS:
      return {
        ...state,
        isLoadingNotifications: action.value,
      }
    case ActionType.SET_LOADER_NOTIFICATION_DETAIL:
      return {
        ...state,
        isLoadingNotificationDetail: action.value,
      }
    case ActionType.SET_NOTIFICATION_DETAIL:
      return {
        ...state,
        notificationDetail: action.value,
        isLoadingNotificationDetail: false,
      }
    case ActionType.SET_INSERT_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.value,
        isLoadingNotifications: false,
      }
    case ActionType.MERGE_NOTIFICATIONS:
      return {
        ...state,
        notifications: {
          ...state.notifications,
          data: state.notifications.data.map((noti) =>
            noti.id === action.value.id ? mergeAll([noti, action.value]) : noti,
          ),
        },
        notificationDetail:
          state.notificationDetail.id === action.value.id
            ? action.value
            : state.notificationDetail,
      }
    case ActionType.MERGE_INFINITE_NOTIFICATIONS:
      return {
        ...state,
        infiniteNotifications: state.infiniteNotifications.map((noti) =>
          noti.id === action.value.id ? mergeAll([noti, action.value]) : noti,
        ),
        notificationDetail:
          state.notificationDetail.id === action.value.id
            ? action.value
            : state.notificationDetail,
      }
    case ActionType.SET_PLACE_EMPTY_NOTIFICATION:
      return {
        ...state,
        isEmptyNotification: true,
      }
    case ActionType.CLEAR_NOTIFICATION_DETAIL:
      return {
        ...state,
        notificationDetail: {},
      }
    case ActionType.SET_LOADER_INFINITE_SCROLL:
      return {
        ...state,
        isInfiniteScroll: action.value,
      }
    case ActionType.SET_INFINITE_NOTIFICATIONS:
      return {
        ...state,
        params: action.params,
        isHasMore: action.isHasMore,
        isInfiniteScroll: false,
        isLoadingNotifications: false,
        notifications: { ...state.notifications, meta: {} },
        infiniteNotifications: action.isScroll
          ? state.infiniteNotifications.concat(action.value)
          : action.value,
        isEmptyNotification: false,
      }
    case ActionType.REMOVE_DATA_FROM_INFINITE_NOTIFICATIONS:
      return {
        ...state,
        infiniteNotifications: state.infiniteNotifications.filter(
          ({ id }) => id !== action.id,
        ),
      }
    case ActionType.UPDATE_INFINITE_NOTIFICATIONS:
      return {
        ...state,
        infiniteNotifications: state.infiniteNotifications.map((noti) =>
          noti.id === action.value.id ? mergeAll([noti, action.value]) : noti,
        ),
        notificationDetail: action.value,
        isUpdating: false,
      }
    case ActionType.SET_LOADER_UPDATING:
      return {
        ...state,
        isUpdating: action.value,
      }
    case ActionType.SET_FAVORITE:
      return {
        ...state,
        notifications: action.value.notifications,
        notificationDetail: action.value.notificationDetail,
        infiniteNotifications: action.value.infiniteNotifications,
        isEmptyNotification: action.value.isEmptyNotification,
      }
    case ActionType.SET_FAVORITE_LOADING:
      return {
        ...state,
        isFavoriteLoading: action.payload,
      }
    default:
      return state
  }
}

export default function useTimeline(isFavoritePath) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()
  const user = useSelector(({ auth }) => auth.user.data)

  const handleAlertError = useCallback(() => {
    return commonDispatch(
      MessageAlert.showError({
        message: formatMessage({
          id: 'error.general',
        }),
      }),
    )
  }, [commonDispatch, formatMessage])

  const fetchNotifications = useCallback(
    (
      params = initialState.params,
      view,
      isFirstTime = false,
      isScroll = false,
    ) => {
      effects.apis
        .notificationsList({ ...params, isFavoritePath })
        .then((response) => {
          if (isFirstTime && response.meta.total === 0)
            dispatch({
              type: 'SET_PLACE_EMPTY_NOTIFICATION',
            })
          else if (view === CARD_VIEW) {
            dispatch({
              type: 'SET_INFINITE_NOTIFICATIONS',
              value: response.data,
              params: { ...params, isFavoritePath },
              isScroll: isScroll,
              isHasMore: response.meta.currentPage < response.meta.lastPage,
            })
          } else if (view === TABLE_VIEW) {
            dispatch({
              type: 'SET_NOTIFICATIONS',
              value: response,
              params: { ...params, isFavoritePath },
            })
          }
        })
        .catch(() => handleAlertError())
    },
    [handleAlertError],
  )

  const debounceFetchNotifications = useCallback(
    debounce((value, view) => {
      dispatch({ type: 'SET_LOADER_NOTIFICATIONS', value: true })
      fetchNotifications(value, view)
    }, 1000),
    [],
  )

  const handleFetchNotifications = useCallback(
    async (params, view, isFirstTime = false) => {
      dispatch({ type: 'SET_LOADER_NOTIFICATIONS', value: true })
      await fetchNotifications(params, view, isFirstTime)
    },
    [fetchNotifications],
  )

  const handleLoading = (view) => {
    if (view === TABLE_VIEW)
      dispatch({ type: 'SET_LOADER_NOTIFICATIONS', value: true })
    else if (view === CARD_VIEW)
      dispatch({ type: 'SET_LOADER_INFINITE_SCROLL', value: true })
    else return
  }

  const handlePages = async (pageNumber, view, isScroll = false) => {
    await handleLoading(view)
    await fetchNotifications(
      { ...state.params, page: pageNumber },
      view,
      false,
      isScroll,
    )
  }

  const handleChangeTabs = async (view) => {
    dispatch({ type: 'SET_LOADER_NOTIFICATIONS', value: true })
    await fetchNotifications({ ...state.params, page: 1 }, view)
  }

  const handleDateFilter = ({ startDate = '', endDate = '' }, view) => {
    const startEndDate = {
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
    }

    const setParams = mergeAll([
      { ...state.params, ...startEndDate, page: 1, limit: 20 },
    ])
    handleFetchNotifications(setParams, view)
  }

  const handleTypeFilter = (typeSelected = [], view) => {
    let selectedId = []
    if (isEmpty(typeSelected)) selectedId = initialState.params.type
    else selectedId = typeSelected.map((selected) => selected.id)

    const setParams = mergeAll([
      { ...state.params, type: selectedId.toString(), page: 1, limit: 20 },
    ])
    handleFetchNotifications(setParams, view)
  }

  const handleFilter = (selected = [], key = [], view) => {
    let selectedObj = {}
    const reduceArrayToObject = reduce(
      (obj, item) => assoc(item.name, item, obj),
      {},
      selected,
    )

    timelineFilter[key].forEach((selected) => {
      selectedObj[selected.name] =
        reduceArrayToObject[selected.name]?.value ?? 0
    })

    const checkSelectedFilter = reject(equals(0))(
      mergeAll([{ ...state.params, ...selectedObj, page: 1, limit: 20 }]),
    )

    handleFetchNotifications(checkSelectedFilter, view)
  }

  const handleSearch = (keyword, view) => {
    const setParams = reject(equals(''))(
      mergeAll([{ ...state.params, keyword: keyword, page: 1, limit: 20 }]),
    )
    debounceFetchNotifications(setParams, view)
  }

  const handleResetFilter = (tabView) => {
    handleFetchNotifications({ ...initialState.params }, tabView)
  }

  const handleSort = (sort, view) => {
    dispatch({ type: 'SET_LOADER_NOTIFICATIONS', value: true })
    fetchNotifications(
      {
        ...state.params,
        sort: sort,
      },
      view,
    )
  }

  const fetchNotificationsById = (id, languageId = null) => {
    effects.apis
      .notificationDetail({ notiId: id, language: languageId })
      .then((response) => {
        dispatch({ type: 'SET_NOTIFICATION_DETAIL', value: response })
      })
      .catch(() => handleAlertError())
  }

  const handleFetchNotificationsById = (id, languageId = null) => {
    dispatch({ type: 'SET_LOADER_NOTIFICATION_DETAIL', value: true })
    fetchNotificationsById(id, languageId)
  }

  const handleFetchUpdateSocket = useCallback(
    (value, languageId = null, tabView) => {
      effects.apis
        .notificationDetail({
          notiId: value.id,
          language: languageId,
        })
        .then((response) => {
          if (tabView === TABLE_VIEW) {
            dispatch({
              type: 'MERGE_NOTIFICATIONS',
              value: response,
            })
          } else if (tabView === CARD_VIEW) {
            dispatch({
              type: 'MERGE_INFINITE_NOTIFICATIONS',
              value: response,
            })
          }
        })
        .catch(() => handleAlertError())
    },
    [handleAlertError],
  )

  const handleClearNotificationDetail = useCallback(() => {
    dispatch({
      type: 'CLEAR_NOTIFICATION_DETAIL',
    })
  }, [])

  const handleResponseInfiniteNotification = (noti) => {
    if (noti.type === TYPE_POLL) {
      effects.apis
        .pollByNotification(noti.id, noti.poll.id, null)
        .then((poll) => {
          dispatch({
            type: 'UPDATE_INFINITE_NOTIFICATIONS',
            value: {
              ...noti,
              poll: mergeAll([noti.poll, poll]),
            },
          })
        })
        .catch(() => handleAlertError())
    } else
      dispatch({
        type: 'UPDATE_INFINITE_NOTIFICATIONS',
        value: noti,
      })
  }

  const handleUpdateTimelineCardView = (id, status = null, obj = {}) => {
    if (status === DELETED_MSG) {
      dispatch({
        type: 'REMOVE_DATA_FROM_INFINITE_NOTIFICATIONS',
        id: id,
      })
    } else if (status === UPDATED_MSG) {
      effects.apis
        .notificationDetail({
          notiId: id,
          language: null,
        })
        .then((response) => {
          handleResponseInfiniteNotification({ ...response, ...obj })
        })
        .catch(() => handleAlertError())
    }
  }

  const handleUpdating = (bool) => {
    dispatch({ type: 'SET_LOADER_UPDATING', value: bool })
  }

  const handleUpdateDetailAndTimelineTableView = (id, params, tabView) => {
    fetchNotificationsById(id)
    fetchNotifications(params, tabView)
  }

  const updateFavoriteToState = (alertId, isFavorite, view) => {
    const newNotificaions =
      isFavoritePath && view === CARD_VIEW
        ? state.notifications.data.filter((item) => item.id !== alertId)
        : state.notifications.data.map((item) => {
            if (alertId === item.id) {
              item.isFavorited = !item.isFavorited
            }
            return item
          })
    const newInfiniteNotifications = isFavoritePath
      ? state.infiniteNotifications.filter((item) => item.id !== alertId)
      : state.infiniteNotifications.map((item) => {
          if (alertId === item.id) {
            item.isFavorited = !item.isFavorited
          }
          return item
        })

    const newNotificationDetail = isEmptyOrNil(state.notificationDetail)
      ? state.notificationDetail
      : {
          ...state.notificationDetail,
          isFavorited: !state.notificationDetail.isFavorited,
        }

    dispatch({
      type: 'SET_FAVORITE',
      value: {
        notifications: {
          ...state.notifications,
          data: newNotificaions,
          meta:
            view === CARD_VIEW
              ? {}
              : {
                  ...state.notifications.meta,
                  total: state.notifications.meta.total - 1,
                },
        },
        infiniteNotifications: newInfiniteNotifications,
        notificationDetail: newNotificationDetail,
        isEmptyNotification: isFavorite
          ? view === TABLE_VIEW
            ? state?.notifications?.meta?.total === 1
            : newInfiniteNotifications.length === 0
          : false,
      },
    })
  }

  const updateFavorites = async (alertId, isFavorite, view) => {
    dispatch({
      type: ActionType.SET_FAVORITE_LOADING,
      payload: true,
    })
    updateFavoriteToState(alertId, isFavorite, view)

    const effectsApi = isFavorite
      ? effects.apis.favotites.remove
      : effects.apis.favotites.add

    effectsApi({
      userId: user.id,
      alertId,
    })
      .then(() => {
        const messageId = isFavorite
          ? 'success.remove.favorite'
          : 'success.add.favorite'
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({ id: messageId }),
          }),
        )
      })
      .catch(() => {
        commonDispatch(
          MessageAlert.showError({
            message: formatMessage({
              id: 'error.add.favorite',
            }),
          }),
        )
      })
      .finally(() => {
        if (view === TABLE_VIEW) {
          const calPage =
            state.notifications.meta.lastPage > 1 &&
            state.notifications.data.length === 1 &&
            isFavorite
              ? state.params.page - 1
              : state.params.page
          fetchNotifications({ ...state.params, page: calPage }, view)
        }

        dispatch({
          type: ActionType.SET_FAVORITE_LOADING,
          payload: false,
        })
      })
  }

  return {
    state,
    handleSort,
    handlePages,
    handleSearch,
    handleFilter,
    handleUpdating,
    handleDateFilter,
    handleChangeTabs,
    handleTypeFilter,
    handleResetFilter,
    fetchNotifications,
    handleFetchUpdateSocket,
    handleFetchNotifications,
    handleFetchNotificationsById,
    handleUpdateTimelineCardView,
    handleClearNotificationDetail,
    handleUpdateDetailAndTimelineTableView,
    updateFavorites,
  }
}
